
import React, { useContext, useMemo, useState } from "react";
import { ITicket } from "../../types/dashboard";
import "./CalendarGridDay.css";
import { formatTimeForInputValue, getTimezoneAwareDate, getTimezoneAwareMonth, getTimezoneAwareYear, getTimezoneAwareMinutes, setTimezoneAwareHours } from "../../utils/dateFormats";
import { AppContext } from "../../appContext";

interface CalendarGridDayProps {
    baseDate?: Date;
    tickets: ITicket[];
    onTimeClick?: (time: Date) => void;
    onTicketClick?: (ticket: ITicket) => void;
}

const CalendarTicket = ({ ticket, ticketIndex, totalTickets, onTicketClick }: { 
    ticket: ITicket; 
    ticketIndex?: number; 
    totalTickets?: number; 
    onTicketClick: () => void 
}) => {
    const getTicketColor = (type: string) => {
        switch(type) {
            case 'session': return 'calendarGridDay_ticket--colorA';
            case 'event': return 'calendarGridDay_ticket--colorB';
            case 'task': return 'calendarGridDay_ticket--colorC';
            default: return 'calendarGridDay_ticket--colorA';
        }
    };

    const ticketHeight = 40;
    const topOffset = ticketIndex !== undefined && totalTickets && totalTickets > 1
        ? 2 + (ticketIndex * (ticketHeight + 2))
        : 2;

    return (
        <div
            className={`calendarGridDay_ticket ${getTicketColor(ticket.type)}`}
            onClick={onTicketClick}
            style={{
                "--offsetTime": `${getTimezoneAwareMinutes(ticket.start) / 60 * 100}%`,
                "--duration": `${(ticket.end.valueOf() - ticket.start.valueOf()) / (1000 * 60 * 60) * 100}%`,
                top: `${topOffset}px`,
                height: `${ticketHeight}px`,
                minHeight: `${ticketHeight}px`
            } as any}
        >
            <div className="calendarGridDay_ticket_content">
                <div className="calendarGridDay_ticket_time">
                    {formatTimeForInputValue(ticket.start)} - {formatTimeForInputValue(ticket.end)}
                </div>
                <div className="calendarGridDay_ticket_title">
                    {ticket.title || 'New Event'}
                </div>
            </div>
        </div>
    );
};

export const CalendarGridDay: React.FC<CalendarGridDayProps> = ({
    baseDate: _baseDate,
    tickets,
    onTimeClick,
    onTicketClick
}) => {
    const baseDate = _baseDate || new Date();
    const { translate } = useContext(AppContext);
    const timeSlots = Array.from({ length: 24 }, (_, i) => i);

    const dayTickets = useMemo(() => {
        return tickets.filter(ticket => {
            const ticketDate = ticket.start;
            return getTimezoneAwareDate(ticketDate) === getTimezoneAwareDate(baseDate) &&
                   getTimezoneAwareMonth(ticketDate) === getTimezoneAwareMonth(baseDate) &&
                   getTimezoneAwareYear(ticketDate) === getTimezoneAwareYear(baseDate);
        });
    }, [tickets, baseDate]);

    const handleTimeClick = (hour: number) => {
        const clickTime = setTimezoneAwareHours(baseDate, hour, 0, 0, 0);
        onTimeClick?.(clickTime);
    };

    const getTicketsForHour = (hour: number) => {
        const startTime = setTimezoneAwareHours(baseDate, hour, 0, 0, 0);
        const endTime = setTimezoneAwareHours(baseDate, hour + 1, 0, 0, 0);

        return dayTickets.filter(ticket => {
            return ticket.start >= startTime && ticket.start < endTime;
        });
    };

    const formatDate = (date: Date) => {
        const options: Intl.DateTimeFormatOptions = { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        };
        return date.toLocaleDateString(undefined, options);
    };

    return (
        <div className="calendarGridDay">
            <div className="calendarGridDay_header">
                <h2 className="calendarGridDay_date">{formatDate(baseDate)}</h2>
            </div>
            
            <div className="calendarGridDay_container">
                <div className="calendarGridDay_scroll_container">
                    <div className="calendarGridDay_body">
                        {timeSlots.map(hour => {
                            const ticketsInSlot = getTicketsForHour(hour);
                            const rowHeight = ticketsInSlot.length > 1
                                ? `${2 + (ticketsInSlot.length * (40 + 2)) + 5}px`
                                : '60px';

                            return (
                                <div key={hour} className="calendarGridDay_time_row" style={{ minHeight: rowHeight }}>
                                    <div className="calendarGridDay_time_slot">
                                        {hour.toString().padStart(2, '0')}:00
                                    </div>
                                    <div
                                        className="calendarGridDay_cell"
                                        onClick={() => handleTimeClick(hour)}
                                        style={{ minHeight: rowHeight }}
                                    >
                                        {ticketsInSlot.map((ticket, ticketIndex) => (
                                            <CalendarTicket
                                                key={ticketIndex}
                                                ticket={ticket}
                                                ticketIndex={ticketIndex}
                                                totalTickets={ticketsInSlot.length}
                                                onTicketClick={() => onTicketClick?.(ticket)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
